import { stripCredentials } from './credentials.js';
import { LEGACY_ENDPOINTS, type LegacyEndpointName } from './endpoints.js';
import { normaliseEnvelope } from './envelope.js';
import { isInvalidTokenReply } from '../session.js';
import type { LegacyResult } from './types.js';

/**
 * Client for the legacy MyPoz OutterApi.
 *
 * Separate from ApiClient because the two authenticate differently: ApiClient sends
 * `Authorization: Bearer`, while this API expects `token` as a field in the request body. Keeping
 * them apart means neither has to grow a mode flag, and if a proxy later normalises the legacy
 * API into the modern shape, this class is deleted rather than untangled.
 *
 * Every call goes through the endpoint registry, so a caller cannot invent a path or omit the
 * verb. That is deliberate: this API answers a wrong-verb request with HTTP 200 and the rejection
 * buried in the body, which surfaces as an empty screen rather than an error.
 */
export interface LegacyApiOptions {
  /** Base URL up to and including /OutterApi/Api. */
  baseUrl: string;
  /** Returns the current token, or null when signed out. */
  getToken?: () => string | null;
  /** Injected in tests; defaults to global fetch. */
  fetchFn?: typeof fetch;
  /** Abandon a request after this many ms. */
  timeoutMs?: number;
  /**
   * Called when the vendor says the token is missing, wrong or expired (MYP-125) — before the
   * result is returned, so the app can switch to its signed-out screen however the caller handles
   * the failure. Recognised from the raw reply (`isInvalidTokenReply`), since several endpoints use
   * the same `-1` code for other refusals.
   */
  onInvalidToken?: () => void;
}

/**
 * Encodes the request body as `application/x-www-form-urlencoded`.
 *
 * Not JSON, and this is not a style choice. The vendor is PHP and reads its parameters out of
 * `$_POST`, which a JSON request body never populates. A JSON call still answers HTTP 200 with
 * `code: 1` and a payload — it simply ignores every parameter and returns the unfiltered default
 * page. Verified live against `package_list`: as JSON, `id`, `page`, `limit`, `status`,
 * `goodstype` and `validStatus` were all inert and the same ten rows came back every time; as
 * form-encoded, every one of them works.
 *
 * That failure is invisible from the outside, which is exactly the class of bug this client exists
 * to prevent. It went unnoticed until now only because `warehouses` — the one endpoint wired so
 * far (MYP-30) — takes no parameters at all, so it had nothing to lose.
 *
 * Arrays are comma-joined: the API's own multi-value convention (`pkgservices: "5,6"`,
 * `status: "IN,ORDER"`). `undefined` and `null` are dropped rather than sent as the strings
 * "undefined"/"null", which the vendor would treat as real filter values.
 */
function encodeForm(params: Record<string, unknown>): string {
  const form = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    form.set(key, Array.isArray(value) ? value.join(',') : String(value));
  }

  return form.toString();
}

export class LegacyApiClient {
  constructor(private readonly opts: LegacyApiOptions) {}

  async call<T>(
    name: LegacyEndpointName,
    params: Record<string, unknown> = {},
  ): Promise<LegacyResult<T>> {
    const endpoint = LEGACY_ENDPOINTS[name];
    const fetchFn = this.opts.fetchFn ?? globalThis.fetch;
    const token = this.opts.getToken?.() ?? undefined;

    const controller = new AbortController();
    const timeout =
      this.opts.timeoutMs === undefined
        ? undefined
        : setTimeout(() => controller.abort(), this.opts.timeoutMs);

    let response: Response;
    try {
      response = await fetchFn(`${this.opts.baseUrl}${endpoint.path}`, {
        // From the registry, never a default — see the class comment.
        method: endpoint.method,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        // The token travels in the body: this API has no Authorization header.
        body: encodeForm({ ...(token ? { token } : {}), ...params }),
        signal: controller.signal,
      });
    } catch (error) {
      const aborted = error instanceof Error && error.name === 'AbortError';
      return {
        ok: false,
        code: aborted ? 'TIMEOUT' : 'NETWORK',
        message: aborted ? 'The request timed out' : 'Could not reach the server',
      };
    } finally {
      if (timeout !== undefined) clearTimeout(timeout);
    }

    if (!response.ok) {
      return {
        ok: false,
        code: `HTTP_${response.status}`,
        message: response.statusText || 'Request failed',
      };
    }

    const text = await response.text();
    if (text.trim() === '') {
      return { ok: false, code: 'EMPTY', message: 'Server returned an empty response' };
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      // A gateway error page arrives as HTML with a 200 more often than anyone would like.
      return { ok: false, code: 'MALFORMED', message: 'Response was not valid JSON' };
    }

    /*
      Credentials come off here, before anything above the transport can see them — see
      `stripCredentials`. Done to the parsed body rather than per adapter because the adapters were
      never the risk: they read named fields. The risk is a caller keeping the raw response.
    */
    if (isInvalidTokenReply(parsed)) this.opts.onInvalidToken?.();

    return normaliseEnvelope<T>(endpoint, stripCredentials(parsed));
  }
}
